import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Upload, Trash2, RefreshCw, FileSpreadsheet, CheckCircle2,
  AlertCircle, DownloadCloud, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (v) => (typeof v === 'number'
  ? v.toLocaleString('pt-PT', { maximumFractionDigits: 0 })
  : '—');

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'dd MMM yyyy', { locale: pt }); }
  catch { return iso; }
};

const ConfiguracaoEnergia = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [locais, setLocais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [localOverride, setLocalOverride] = useState('');

  const loadLocais = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/energia/locais`);
      const d = await r.json();
      setLocais(Array.isArray(d) ? d : []);
    } catch (e) {
      toast.error('Erro ao carregar locais');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLocais(); }, [loadLocais]);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append('file', file);
      if (localOverride) fd.append('local_override', localOverride);
      try {
        const r = await fetch(`${API_URL}/api/energia/upload`, { method: 'POST', body: fd });
        const d = await r.json();
        if (r.ok && d.success) {
          ok += 1;
          toast.success(`${file.name}: ${fmt(d.rows_processed)} leituras importadas (${d.local})`);
        } else {
          fail += 1;
          toast.error(`${file.name}: ${d.detail || 'erro'}`);
        }
      } catch (err) {
        fail += 1;
        toast.error(`${file.name}: ${err.message}`);
      }
    }
    setUploading(false);
    setLocalOverride('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    await loadLocais();
    if (ok && !fail) toast.success(`${ok} ficheiro(s) importado(s) com sucesso`);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFiles(e.dataTransfer.files);
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const r = await fetch(`${API_URL}/api/energia/seed`, { method: 'POST' });
      const d = await r.json();
      if (r.ok && d.success) {
        toast.success(`Seed concluído: ${d.loaded} ficheiro(s) carregados`);
        const failures = (d.results || []).filter((x) => !x.ok);
        if (failures.length) {
          toast.error(`${failures.length} falharam`);
        }
      } else {
        toast.error('Falha no seed');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSeeding(false);
      await loadLocais();
    }
  };

  const handleRemove = async (cpe) => {
    try {
      const r = await fetch(`${API_URL}/api/energia/local/${cpe}`, { method: 'DELETE' });
      const d = await r.json();
      if (r.ok && d.success) {
        toast.success('Local removido');
        await loadLocais();
      } else {
        toast.error(d.detail || 'Erro ao remover');
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="config-energia-page">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/energia')}
          data-testid="config-energia-back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Configuração — Energia</h1>
          <p className="text-gray-600 mt-1">
            Importação de Diagramas de Carga (E-Redes) em formato XLSX
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Upload className="h-5 w-5 text-[#003D7A]" /> Importar ficheiros
          </CardTitle>
          <CardDescription>
            Cada ficheiro deve conter as colunas <strong>Data</strong>, <strong>Fuso</strong> e
            <strong> Potência média</strong> (kW). O consumo em kWh é calculado como
            <code className="mx-1 px-1 py-0.5 bg-gray-100 rounded text-xs">potência × 0,25h</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="local-override" className="text-sm">Nome do local (opcional)</Label>
            <Input
              id="local-override"
              data-testid="config-energia-local-override"
              placeholder="Deixe vazio para usar o nome detetado pelo CPE"
              value={localOverride}
              onChange={(e) => setLocalOverride(e.target.value)}
              className="mt-1 max-w-md"
            />
            <p className="text-xs text-gray-500 mt-1">
              Sobrescreve o nome do local apenas se o CPE for novo. Os 8 CPEs do cliente já estão mapeados automaticamente.
            </p>
          </div>

          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={(e) => e.preventDefault()}
            className="border-2 border-dashed border-gray-300 rounded-lg p-10 text-center hover:border-[#003D7A] transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
            data-testid="config-energia-dropzone"
          >
            <FileSpreadsheet className="h-10 w-10 mx-auto text-gray-400 mb-3" />
            <p className="text-sm font-medium text-gray-700">
              Arraste ficheiros XLSX para aqui ou clique para selecionar
            </p>
            <p className="text-xs text-gray-500 mt-1">Suporte para múltiplos ficheiros simultâneos</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              multiple
              hidden
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="config-energia-file-input"
            />
            {uploading && (
              <div className="mt-3 flex items-center justify-center gap-2 text-[#003D7A] text-sm">
                <RefreshCw className="h-4 w-4 animate-spin" />
                A importar...
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 pt-2 border-t">
            <Button
              variant="outline"
              onClick={handleSeed}
              disabled={seeding}
              data-testid="config-energia-seed-btn"
            >
              <DownloadCloud className={`h-4 w-4 mr-2 ${seeding ? 'animate-pulse' : ''}`} />
              {seeding ? 'A carregar seed...' : 'Carregar seed (ficheiros do cliente)'}
            </Button>
            <p className="text-xs text-gray-500">
              Carrega automaticamente os ficheiros já fornecidos pelo cliente.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#003D7A]" /> Locais carregados
            </CardTitle>
            <CardDescription>
              {locais.length === 0 ? 'Nenhum local carregado' : `${locais.length} locais com dados energéticos`}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={loadLocais}
            disabled={loading}
            data-testid="config-energia-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Local</TableHead>
                <TableHead>CPE</TableHead>
                <TableHead>Período</TableHead>
                <TableHead className="text-right">Leituras</TableHead>
                <TableHead className="text-right">Consumo Total</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locais.map((l) => (
                <TableRow key={l.cpe} data-testid={`config-energia-row-${l.cpe}`}>
                  <TableCell>
                    <div className="font-medium text-gray-900">{l.local}</div>
                    {l.owner && (
                      <div className="text-xs text-gray-500">{l.owner}</div>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{l.cpe}</TableCell>
                  <TableCell className="text-sm">
                    {fmtDate(l.period_start)} → {fmtDate(l.period_end)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(l.rows_total)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {fmt(l.total_consumo_kwh)} kWh
                  </TableCell>
                  <TableCell className="text-right">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`config-energia-delete-${l.cpe}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remover local?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Vai remover todas as leituras de <strong>{l.local}</strong>. Esta operação não pode ser anulada.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleRemove(l.cpe)}>
                            Remover
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
              {!locais.length && !loading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-gray-500 py-10">
                    <AlertCircle className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                    Sem locais carregados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfiguracaoEnergia;
